import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, X, Search, ArrowUpDown, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { toast } from 'sonner';
import ProjectCard from '@/components/ProjectCard';

const causeAreas = [
  { value: 'all', label: 'All Causes' },
  { value: 'hunger', label: 'Hunger' },
  { value: 'education', label: 'Education' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'shelter', label: 'Shelter' },
  { value: 'refugees', label: 'Refugees' },
  { value: 'children', label: 'Children' },
  { value: 'environment', label: 'Environment' },
  { value: 'disaster-response', label: 'Disaster Response' },
];

const fundingStages = [
  { value: 'all', label: 'All Stages' },
  { value: 'just-started', label: 'Just Started' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'nearly-funded', label: 'Nearly Funded' },
  { value: 'complete', label: 'Complete' },
];

const locations = [
  { value: 'all', label: 'All Locations' },
  { value: 'USA', label: 'United States' },
  { value: 'Kenya', label: 'Kenya' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'India', label: 'India' },
  { value: 'Jordan', label: 'Jordan' },
  { value: 'Brazil', label: 'Brazil' },
  { value: 'Philippines', label: 'Philippines' },
  { value: 'Germany', label: 'Germany' },
  { value: 'Uganda', label: 'Uganda' },
  { value: 'Rwanda', label: 'Rwanda' },
  { value: 'Bangladesh', label: 'Bangladesh' },
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'Nepal', label: 'Nepal' },
  { value: 'Peru', label: 'Peru' },
  { value: 'Turkey', label: 'Turkey' },
  { value: 'Haiti', label: 'Haiti' },
  { value: 'Tanzania', label: 'Tanzania' },
  { value: 'Caribbean', label: 'Caribbean' },
];

const sortOptions = [
  { value: 'most-funded', label: 'Most Funded' },
  { value: 'least-funded', label: 'Least Funded' },
  { value: 'newest', label: 'Newest First' },
  { value: 'most-donors', label: 'Most Donors' },
  { value: 'nearly-complete', label: 'Nearly Complete' },
];

function FilterSidebar({ cause, setCause, status, setStatus, location, setLocation, searchQuery, setSearchQuery }) {
  return (
    <div className="space-y-6" data-testid="filter-sidebar">
      <div>
        <label className="text-sm font-medium text-[#111827] mb-2 block font-heading">Search</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl border-gray-200 font-body"
            data-testid="filter-search"
          />
        </div>
      </div>
      <div>
        <label className="text-sm font-medium text-[#111827] mb-2 block font-heading">Cause Area</label>
        <Select value={cause} onValueChange={setCause}>
          <SelectTrigger className="rounded-xl border-gray-200" data-testid="filter-cause">
            <SelectValue placeholder="All Causes" />
          </SelectTrigger>
          <SelectContent>
            {causeAreas.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium text-[#111827] mb-2 block font-heading">Location</label>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className="rounded-xl border-gray-200" data-testid="filter-location">
            <SelectValue placeholder="All Locations" />
          </SelectTrigger>
          <SelectContent>
            {locations.map(l => (
              <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-sm font-medium text-[#111827] mb-2 block font-heading">Funding Stage</label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-xl border-gray-200" data-testid="filter-status">
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            {fundingStages.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(cause !== 'all' || status !== 'all' || location !== 'all' || searchQuery) && (
        <Button
          variant="ghost"
          className="w-full text-sm text-gray-500 hover:text-[#111827] font-body"
          onClick={() => { setCause('all'); setStatus('all'); setLocation('all'); setSearchQuery(''); }}
          data-testid="filter-clear"
        >
          <X className="w-4 h-4 mr-1" /> Clear Filters
        </Button>
      )}
    </div>
  );
}

function SocialProofBadge({ project }) {
  const donorCount = Math.floor((project.fundingRaised || 0) / 150) + Math.floor(Math.random() * 10 + 5);
  const avatarSeeds = Array.from({ length: Math.min(4, donorCount) }, (_, i) => project.id * 7 + i);
  return (
    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
      <div className="flex -space-x-1.5">
        {avatarSeeds.map((seed, i) => (
          <img key={i} src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`} alt="" className="w-5 h-5 rounded-full border-2 border-white" />
        ))}
      </div>
      <span className="text-xs text-gray-500 font-body flex items-center gap-1">
        <Users className="w-3 h-3" /> {donorCount} donors
      </span>
    </div>
  );
}

function EnhancedProjectCard({ project, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <ProjectCard project={project} />
    </motion.div>
  );
}

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cause, setCause] = useState('all');
  const [status, setStatus] = useState('all');
  const [location, setLocation] = useState('all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [sortBy, setSortBy] = useState('most-funded');
  const [mobileFilters, setMobileFilters] = useState(false);

  // Sync search query with URL params
  useEffect(() => {
    const q = searchParams.get('q');
    if (q && q !== searchQuery) setSearchQuery(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const all = await fetch('/data/projects.json').then(r => r.json());
        let filtered = all;
        if (cause !== 'all') filtered = filtered.filter(p => p.causeArea === cause);
        if (status !== 'all') filtered = filtered.filter(p => p.status === status);
        if (location !== 'all') filtered = filtered.filter(p => (p.location || '').toLowerCase().includes(location.toLowerCase()));
        setProjects(filtered);
      } catch (err) {
        toast.error('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [cause, status, location]);

  // Update URL when search changes
  useEffect(() => {
    if (searchQuery) {
      setSearchParams({ q: searchQuery }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [searchQuery, setSearchParams]);

  let filteredProjects = searchQuery
    ? projects.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.ngoName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [...projects];

  // Sort
  filteredProjects.sort((a, b) => {
    switch (sortBy) {
      case 'most-funded': return (b.fundingRaised || 0) - (a.fundingRaised || 0);
      case 'least-funded': return (a.fundingRaised || 0) - (b.fundingRaised || 0);
      case 'newest': return (b.id || 0) - (a.id || 0);
      case 'most-donors': return (b.fundingRaised || 0) / 150 - (a.fundingRaised || 0) / 150;
      case 'nearly-complete': return ((b.fundingRaised || 0) / (b.fundingGoal || 1)) - ((a.fundingRaised || 0) / (a.fundingGoal || 1));
      default: return 0;
    }
  });

  const activeFilterCount = [cause !== 'all', status !== 'all', location !== 'all', !!searchQuery].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="explore-page">
      <div className="bg-gradient-to-br from-purple-50/60 via-white to-blue-50/40 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#7C3AED] mb-3 font-body">
              Explore
            </p>
            <h1 className="text-4xl sm:text-5xl tracking-tighter font-semibold text-[#111827] mb-3 font-heading">
              Discover Verified Projects
            </h1>
            <p className="text-base lg:text-lg text-[#4B5563] max-w-2xl leading-relaxed font-body">
              Every project on HumaClub has been verified on-chain. Browse by cause, location, or funding stage.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="lg:hidden mb-6">
          <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
            <SheetTrigger asChild>
              <Button variant="outline" className="rounded-xl border-gray-200 gap-2 font-body" data-testid="mobile-filter-toggle">
                <Filter className="w-4 h-4" />
                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80">
              <SheetTitle className="font-heading font-semibold text-lg mb-6">Filters</SheetTitle>
              <FilterSidebar
                cause={cause} setCause={setCause}
                status={status} setStatus={setStatus}
                location={location} setLocation={setLocation}
                searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              />
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-8">
          <div className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24 bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <h3 className="font-heading font-semibold text-[#111827] mb-5">Filters</h3>
              <FilterSidebar
                cause={cause} setCause={setCause}
                status={status} setStatus={setStatus}
                location={location} setLocation={setLocation}
                searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              />
            </div>
          </div>

          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#4B5563] font-body" data-testid="project-count">
                {loading ? 'Loading...' : `${filteredProjects.length} projects found`}
              </p>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[160px] h-9 rounded-lg border-gray-200 text-xs" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                    <div className="h-48 bg-gray-100 animate-pulse" />
                    <div className="p-5 space-y-3">
                      <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
                      <div className="h-5 bg-gray-100 rounded animate-pulse" />
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-4/5" />
                      <div className="h-2 bg-gray-100 rounded-full animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="text-center py-20" data-testid="no-results">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg text-[#111827] mb-2">No projects found</h3>
                <p className="text-sm text-[#4B5563] font-body">Try adjusting your filters to discover more projects.</p>
              </div>
            ) : (
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {filteredProjects.map((project, i) => (
                  <EnhancedProjectCard key={project.id} project={project} index={i} />
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
